/**
 * @tanstack/react-query hooks for the WFH module (migration 063). Thin
 * wrappers over lib/api/wfh.ts; mirrors use-leave.ts conventions.
 *
 * Query-key root is ["wfh"] (its own root, like ["leave"]). A decision also
 * touches the notification bell (the counterparty is notified), so mutations
 * flush ["notifications"] too.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as wfhApi from "@/lib/api/wfh";
import { invalidateApprovalsSummary } from "@/lib/approvals-invalidate";
import type {
  CreateWFHRequest,
  WFHDecisionRequest,
} from "@/types/wfh";

export const wfhKeys = {
  all: ["wfh"] as const,
  requests: (status?: string, scope?: string, excludeSelf?: boolean) =>
    [
      ...wfhKeys.all,
      "requests",
      status ?? "all",
      excludeSelf ? "excl-self" : (scope ?? "role"),
    ] as const,
  detail: (id: number) => [...wfhKeys.all, "detail", id] as const,
};

const notificationsRoot = ["notifications"] as const;

export function useWFHRequests(
  status?: string,
  scope?: "self",
  opts?: { excludeSelf?: boolean },
) {
  return useQuery({
    queryKey: wfhKeys.requests(status, scope, opts?.excludeSelf),
    queryFn: ({ signal }) =>
      wfhApi.listWFHRequests(
        {
          ...(status ? { status } : {}),
          ...(scope ? { scope } : {}),
          ...(opts?.excludeSelf ? { exclude_self: true } : {}),
        },
        signal,
      ),
  });
}

export function useWFHRequest(id: number) {
  return useQuery({
    queryKey: wfhKeys.detail(id),
    queryFn: ({ signal }) => wfhApi.getWFHRequest(id, signal),
    enabled: Number.isFinite(id) && id > 0,
  });
}

function invalidateWFH(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: wfhKeys.all });
  void qc.invalidateQueries({ queryKey: notificationsRoot });
  // The Inbox / tab badges count pending WFH (phantom-count fix).
  invalidateApprovalsSummary(qc);
}

export function useCreateWFHRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWFHRequest) => wfhApi.createWFHRequest(body),
    onSuccess: () => invalidateWFH(qc),
  });
}

export function useApproveWFHRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: WFHDecisionRequest }) =>
      wfhApi.approveWFHRequest(vars.id, vars.body),
    onSuccess: () => invalidateWFH(qc),
  });
}

export function useRejectWFHRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body?: WFHDecisionRequest }) =>
      wfhApi.rejectWFHRequest(vars.id, vars.body),
    onSuccess: () => invalidateWFH(qc),
  });
}

export function useCancelWFHRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => wfhApi.cancelWFHRequest(id),
    onSuccess: () => invalidateWFH(qc),
  });
}
